import { createContext, type ReactNode, useContext } from 'react';

/**
 * Context for the OSINT partition.
 *
 * When a component tree is wrapped in <OsintProvider>, all descendant
 * queries (useMy, useGetRisks, useQueryAssets, etc.) will automatically
 * target the OSINT partition instead of the user's own partition.
 *
 * This eliminates the need to drill `osint` as a prop through every
 * component and hook in the chain.
 */
interface OsintContextValue {
  isOsint: boolean;
}

const OsintContext = createContext<OsintContextValue>({ isOsint: false });

/**
 * Hook to read OSINT partition context.
 *
 * Components and hooks can call this to determine whether they should
 * query the OSINT partition. If no provider is above them in the tree,
 * defaults to false (normal partition).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useOsintContext(): OsintContextValue {
  return useContext(OsintContext);
}

/**
 * Provider that marks a subtree as operating in the OSINT partition.
 *
 * Usage:
 *   <OsintProvider>
 *     <Vulnerabilities />  // automatically queries OSINT partition
 *   </OsintProvider>
 */
export function OsintProvider({ children }: { children: ReactNode }) {
  return (
    <OsintContext.Provider value={{ isOsint: true }}>
      {children}
    </OsintContext.Provider>
  );
}
